// src/components/issues/ClavierEquipement.tsx — SCE Issues
// Clavier numérique d'office pour chercher un équipement sur téléphone
// (demande de Guillaume du 26/09).
//
// Sur le terrain, on tape surtout des chiffres : « 4825 » trouve 4825-SC, et
// « 4334 » trouve PN4334 (la recherche porte sur « contient », cf.
// searchEquipements). Le pavé numérique évite de chercher les chiffres sur la
// rangée du haut du clavier complet. Pour une saisie qui demande des lettres
// (un CVE, un OF tapé en entier), le bouton « ABC » rend le clavier complet ;
// « 123 » revient au pavé.
//
// `inputMode` ne change que le clavier virtuel : au bureau, rien ne bouge, et
// le bouton n'est montré que sur un écran tactile.

import { useEffect, useRef, useState, type JSX, type RefObject } from 'react'
import { useMediaQuery } from '@/hooks/useMediaQuery'

export function useClavierEquipement(champ: RefObject<HTMLInputElement>): {
  /** À poser sur le champ, avec les réglages qui vont avec une référence. */
  proprietes: {
    inputMode: 'numeric' | 'text'
    autoCapitalize: 'characters'
    autoCorrect: 'off'
    autoComplete: 'off'
    spellCheck: false
    enterKeyHint: 'search'
  }
  /** Bouton « ABC » / « 123 » (écran tactile seulement, sinon null). */
  bascule: JSX.Element | null
} {
  const tactile = useMediaQuery('(pointer: coarse)')
  const [mode, setMode] = useState<'numeric' | 'text'>('numeric')
  const aBascule = useRef(false)

  // Le clavier affiché ne change qu'à la prise de focus (iOS) : on la refait
  // juste après une bascule, le curseur reste où il était.
  useEffect(() => {
    if (!aBascule.current) return
    aBascule.current = false
    const el = champ.current
    if (!el || document.activeElement !== el) return
    el.blur()
    el.focus()
  }, [mode, champ])

  const bascule = tactile ? (
    <button
      type="button"
      // Ne pas voler le focus au champ : le clavier resterait fermé.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        aBascule.current = true
        setMode((m) => (m === 'numeric' ? 'text' : 'numeric'))
        champ.current?.focus()
      }}
      aria-label={mode === 'numeric' ? 'Clavier avec lettres' : 'Clavier numérique'}
      className="flex items-center justify-center"
      style={{
        minWidth: 48,
        height: 36,
        padding: '0 10px',
        borderRadius: 18,
        border: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-gray-5)',
        color: 'var(--color-black)',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {mode === 'numeric' ? 'ABC' : '123'}
    </button>
  ) : null

  return {
    proprietes: {
      inputMode: mode,
      autoCapitalize: 'characters',
      autoCorrect: 'off',
      autoComplete: 'off',
      spellCheck: false,
      enterKeyHint: 'search',
    },
    bascule,
  }
}
